<script setup>
import { Head, router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import ProductCard from '@/Components/Public/ProductCard.vue'
import BrandHero from '@/Components/Public/BrandHero.vue'

defineOptions({ layout: PublicLayout })

const props = defineProps({
  vehicles:   { type: Array,  default: () => [] },
  categories: { type: Array,  default: () => [] },
  filters:    { type: Object, default: () => ({}) },
  base_path:  { type: String, default: '/rentals' }
})

const { t } = useI18n()

function setFilter(key, value) {
  const next = { ...props.filters, [key]: value || undefined }
  Object.keys(next).forEach(k => { if (!next[k]) delete next[k] })
  router.get(props.base_path, next, { preserveScroll: true, preserveState: false })
}
</script>

<template>
  <Head>
    <title>{{ t('public.catalog.head_title') }}</title>
    <meta name="description" :content="t('public.catalog.head_description')">
  </Head>

  <BrandHero kind="vehicle" :title="t('public.catalog.title')">
    <p class="bhero-lede">{{ t('public.catalog.lede') }}</p>
  </BrandHero>

  <section class="listing">
    <div class="listing__inner">
      <div class="toolbar filter-bar">
        <div class="chips">
          <button :class="['chip', !filters.category && 'is-active']" @click="setFilter('category', null)">{{ t('public.catalog.filters.all') }}</button>
          <button v-for="cat in categories" :key="cat" :class="['chip', filters.category === cat && 'is-active']" @click="setFilter('category', cat)">{{ t(`public.categories.${cat}`) }}</button>
        </div>
        <select class="select" :value="filters.sort || 'price_asc'" @change="(e) => setFilter('sort', e.target.value)">
          <option value="price_asc">{{ t('public.catalog.sort.price_asc') }}</option>
          <option value="price_desc">{{ t('public.catalog.sort.price_desc') }}</option>
          <option value="newest">{{ t('public.catalog.sort.newest') }}</option>
        </select>
      </div>

      <div v-if="vehicles.length === 0" class="empty">{{ t('public.catalog.empty') }}</div>

      <ul v-else class="grid">
        <li v-for="v in vehicles" :key="v.id">
          <ProductCard kind="vehicle" :item="v" :href="`${base_path}/${v.id}`" />
        </li>
      </ul>
    </div>
  </section>
</template>

<style scoped>
.bhero-lede { margin: 1rem 0 0; font-size: clamp(1.05rem, 2vw, 1.3rem); line-height: 1.5; max-width: 48ch; opacity: 0.92; }

.listing { background: var(--bg); }
.listing__inner { max-width: 96rem; margin: 0 auto; padding: clamp(1.75rem, 4vw, 2.75rem) clamp(1.25rem, 4vw, 3rem) 4rem; }

.toolbar { display: flex; flex-wrap: wrap; gap: 0.75rem; justify-content: space-between; align-items: center; margin-bottom: 1.75rem; }
.chips { display: flex; gap: 0.4rem; flex-wrap: wrap; }
.chip { padding: 0.5rem 1rem; border-radius: 999px; border: 1px solid var(--line); background: var(--surface); font-size: 0.9375rem; color: var(--inkSoft); cursor: pointer; }
.chip.is-active { background: var(--accent); color: var(--accentInk); border-color: var(--accent); }
.select { padding: 0.5rem 0.85rem; border-radius: 999px; border: 1px solid var(--line); background: var(--surface); color: var(--ink); font-size: 0.9375rem; }

.empty { padding: 4rem 1rem; text-align: center; color: var(--muted); font-size: 1.0625rem; }

.grid { list-style: none; padding: 0; margin: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr)); gap: 1.25rem; }
</style>
